import { trackEvent } from "@analytics";
import { MenuItem, useTheme } from "@biom3/react";

export interface EligibilityItemProps {
  id: "have_linked_any_social_media" | "have_verified_phone";
  icon: "SocialShare" | "ApplicationMobile";
  title: string;
  description: string;
  disabled: boolean;
  completed: boolean;
  onClick?: () => void;
  active?: boolean;
}

export function EligibilityItem({
  id,
  icon,
  title,
  description,
  disabled,
  completed,
  onClick,
  active,
}: EligibilityItemProps) {
  const { base } = useTheme();
  const clickable = !disabled && !completed;

  const onItemClick = () => {
    if (disabled || completed) {
      return;
    }

    trackEvent({
      component: "MenuItem",
      action: "Pressed",
      extras: {
        control: title,
        section: "RewardsEligibility",
        userJourney: "Rewards",
      },
    });

    onClick?.();
  };

  const getIntentIcon = () => {
    if (completed) {
      return {
        icon: "TickWithCircle" as const,
        fill: "base.color.text.status.success.primary",
      };
    }
    if (disabled) {
      return {
        icon: "Lock" as const,
        fill: "base.color.text.body.secondary",
      };
    }
    return {
      icon: "ChevronForward" as const,
      fill: "base.color.text.body.secondary",
    };
  };

  const intentIcon = getIntentIcon();

  return (
    <MenuItem
      sx={{
        bg: "transparent",
        cursor: clickable ? "pointer" : "auto",
        pointerEvents: clickable ? "auto" : "none",
        opacity: disabled ? 0.7 : 1,
      }}
      testId={id}
      onClick={onItemClick}
      key={id}
      title={title}
      disabled={!clickable}
      emphasized={active}
      size={["xSmall", active ? "small" : "xSmall"]}
      motionProfile="fast"
    >
      <MenuItem.Label>{title}</MenuItem.Label>
      {active && <MenuItem.Caption>{description}</MenuItem.Caption>}
      {disabled && id === "have_verified_phone" && (
        <MenuItem.Caption>
          Complete the first two steps to unlock.
        </MenuItem.Caption>
      )}
      <MenuItem.FramedIcon
        icon={icon}
        sx={{
          fill: "base.color.text.body.secondary",
        }}
      />
      <MenuItem.IntentIcon
        icon={intentIcon.icon}
        variant="bold"
        sx={{
          fill: intentIcon.fill,
          transitionDelay: base.motion.normal.gentle.cssDuration,
        }}
      />
    </MenuItem>
  );
}
